import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import { BaseFormField } from "components/form/legacyFields";
import { RawMultiNumericInput } from "components/form/legacyFields/multiNumericInput";
import { RawMultiTimePicker } from "components/form/legacyFields/multiTimePicker";
import { Time } from "components/timePicker";

import * as theme from "./theme.css";

const MAX_DAY_OFFSET = 999;

interface CommonProps {
    days: number[];
    times: Time[];
}

interface EditProps {
    onChangeTime: (time: Time) => void;
    onChangeDays: (days: number) => void;
}

type MultiResponsibleAllocatedTimeInputProps =
    | ({ type: "READ_ONLY" } & CommonProps)
    | ({ type: "EDIT" } & CommonProps & EditProps);

class MultiResponsibleAllocatedTimeInput extends React.Component<MultiResponsibleAllocatedTimeInputProps> {
    render() {
        const { days } = this.props;

        let count = days[0];
        for (const value of days) {
            if (value !== count) {
                // Multiple values => plural
                count = 42;
                break;
            }
        }
        const unitLabel = t("Controls_Schedule_Day", { count });

        const transTargetLabel = t("CreateControl_Notification_ResponsibleHas");
        const taskLabel = t("CreateControl_Notification_Task_Control");
        const toPerformLabel = t(
            "CreateControl_Notification_AssigneeToPerform",
            { taskLabel }
        );
        const pickerLabel = t("CreateControl_Notification_SendTo");

        return (
            <div className={theme.notificationDaysAndTime}>
                <BaseFormField
                    getComponent={() => (
                        <>
                            {this.renderDaysInput()}
                            <span
                                className={theme.notificationDaysTextSpacing}
                            />
                            {`${unitLabel} ${toPerformLabel}`}
                        </>
                    )}
                    hasFeedback={false}
                    label={transTargetLabel}
                />
                <BaseFormField
                    getComponent={() => this.renderTimePicker()}
                    hasFeedback={false}
                    label={pickerLabel}
                />
            </div>
        );
    }

    private renderDaysInput() {
        const props = this.props;
        switch (props.type) {
            case "EDIT": {
                const { days, onChangeDays } = props;
                return (
                    <DaysInput
                        type="EDIT"
                        days={days}
                        onChange={onChangeDays}
                    />
                );
            }
            case "READ_ONLY": {
                const { days } = props;
                return <DaysInput type="READ_ONLY" days={days} />;
            }
            default:
                return assertNever(props);
        }
    }

    private renderTimePicker(): React.ReactNode {
        const props = this.props;

        switch (props.type) {
            case "EDIT": {
                const handleTimeChange = (newTime: Time | undefined) => {
                    if (newTime) {
                        props.onChangeTime(newTime);
                    }
                };
                return (
                    <RawMultiTimePicker
                        type="EDIT"
                        name="time"
                        values={props.times}
                        onChange={handleTimeChange}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <RawMultiTimePicker
                        type="READ_ONLY"
                        name="time"
                        values={props.times}
                    />
                );
            }
            default: {
                return assertNever(props);
            }
        }
    }
}

type DaysInputProps =
    | { type: "READ_ONLY"; days: Array<number> }
    | { type: "EDIT"; days: Array<number>; onChange: (newVal: number) => void };

function DaysInput(props: DaysInputProps) {
    switch (props.type) {
        case "EDIT": {
            const { days, onChange } = props;
            const handleOnChange = (newVal: number | undefined) => {
                if (newVal !== undefined) {
                    onChange(newVal);
                }
            };
            return (
                <RawMultiNumericInput
                    type="CREATE"
                    name="days"
                    min={0}
                    max={MAX_DAY_OFFSET}
                    onChange={handleOnChange}
                    values={days}
                />
            );
        }
        case "READ_ONLY":
            const { days } = props;
            return (
                <RawMultiNumericInput
                    type="READ_ONLY"
                    name="days"
                    display="Input"
                    values={days}
                />
            );
        default:
            return assertNever(props);
    }
}

export { MultiResponsibleAllocatedTimeInput };
